import React, { useMemo } from 'react';
import { Dialog } from '@headlessui/react';
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MappingPreviewSnapshot } from '../../types';

interface MappingPreviewModalProps {
  preview: MappingPreviewSnapshot;
  onClose: () => void;
}

const MappingPreviewModal: React.FC<MappingPreviewModalProps> = ({ preview, onClose }) => {
  const rows = useMemo(() => parseCsv(preview.csv || '', preview.headers.length), [preview.csv, preview.headers.length]);

  const exportCsv = () => {
    const blob = new Blob([preview.csv || ''], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${preview.entityName}-preview.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center overflow-hidden p-4">
        <Dialog.Panel className="relative mx-auto flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div>
              <Dialog.Title className="text-xl font-semibold text-gray-900">
                {preview.entityName} Mapping Preview
              </Dialog.Title>
              <p className="text-sm text-gray-500">
                Generated {new Date(preview.generatedAt).toLocaleString()} • {preview.totalSampledRecords} records across {preview.totalSampledFiles} files
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={exportCsv}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <ArrowDownTrayIcon className="mr-1 h-4 w-4" />
                Export CSV
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {/* Show transformations summary */}
            {preview.sampleRows && preview.sampleRows.some(row => row.transformations && row.transformations.length > 0) && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Applied Transformations:</h3>
                <div className="flex flex-wrap gap-2">
                  {Array.from(new Set(preview.sampleRows.flatMap(row => row.transformations || []))).map(transformation => (
                    <span key={transformation} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {transformation.replace('auto-mapped:', 'Auto: ').replace('constant:', 'Constant: ').replace('value-map:', 'Value Map: ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {preview.headers.map((column, index) => {
                      const isAutoMapped = column === 'userId' || column === 'personIdExternal';
                      return (
                        <th key={column} className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                          isAutoMapped ? 'text-blue-600 bg-blue-50' : 'text-gray-500'
                        }`}>
                          {column}
                          {isAutoMapped && (
                            <span className="ml-1 text-xs normal-case font-normal">(Auto)</span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {rows.map((row, index) => (
                    <tr key={index}>
                      {row.map((value, colIndex) => {
                        const column = preview.headers[colIndex];
                        const isAutoMapped = column === 'userId' || column === 'personIdExternal';
                        return (
                          <td key={`${index}-${colIndex}`} className={`px-4 py-2 text-sm ${
                            isAutoMapped ? 'text-blue-700 bg-blue-50' : 'text-gray-700'
                          }`}>
                            {value}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

function parseCsv(csv: string, expectedColumns: number): string[][] {
  if (!csv) return [];
  return csv.split('\n').filter(Boolean).map(line => {
    const values = line.split(',').map(value => value.replace(/^"|"$/g, ''));
    if (values.length < expectedColumns) {
      return [...values, ...Array(expectedColumns - values.length).fill('')];
    }
    return values.slice(0, expectedColumns);
  }).slice(1);
}

export default MappingPreviewModal;
